import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { NetworkStatusService } from '../../../../core/offline/network-status.service';
import { StoreContextService } from '../../../../core/services/store-context.service';
import { HubDiscoveryService } from '../../../../core/offline/hub-discovery.service';
import { HubRoutingService, hubBlockReasonKey } from '../../../../core/offline/hub-routing.service';
import { HubOriginService } from '../../../../core/offline/hub-origin.service';
import { HubHandoffService } from '../../../../core/offline/hub-handoff.service';
import { HubStalenessService } from '../../../../core/offline/hub-staleness.service';
import { ReconnectPolicyService } from '../../../../core/offline/reconnect-policy.service';
import { environment } from '../../../../../environments/environment';
import { ChangeDetectorRef } from '@angular/core';
import { NetworkState } from '../../../../core/offline/network-status.service';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';

// Hub-only offline (post-IDB-removal): the offline-banner used to also surface
// browser-side queue depth + sync conflicts. With the IndexedDB outbox gone,
// the only offline path is "is the hub reachable?" — variants are simpler.
// (NetworkState import survives Phase E; in Phase F it'll be re-homed off
// offline-types.ts when that file is deleted.)

/**
 * OF-2 / OF-2E / OF-2F — Global offline-state banner.
 *
 * The banner both tells the operator the current state AND offers the
 * two cross-origin switches that OF-2F introduces: Switch to Local Hub
 * (cloud origin → hub HTTP origin) and Switch back to cloud (hub
 * origin → cloud HTTPS origin).
 *
 * Variant matrix (one active at a time):
 *
 *   hidden                   — online + cloud origin
 *   switchToCloud            — hub origin + online
 *   offlineAgentConnected    — hub origin + offline (actively using hub)
 *   offlineHubBlocked        — cloud origin + offline + hub configured but mixed-content
 *   offlineLocalOnly         — cloud origin + offline + no hub / hub unreachable
 *   offlineEnabled           — offline + offlineEnabled (legacy red — covers the hub-down case)
 *   offlineDisabled          — offline + offlineEnabled = false
 *
 *   Note: 'syncing' and 'error' variants were removed with the browser-IDB
 *   outbox queue — the hub now owns drain status.
 */

type BannerVariant =
  | 'hidden'
  | 'offlineEnabled'
  | 'offlineLocalOnly'
  | 'offlineAgentConnected'
  | 'offlineHubBlocked'
  | 'switchToCloud'
  | 'serverAvailable'
  | 'offlineDisabled';

@Component({
  selector: 'app-offline-banner',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="of2-banner" *ngIf="variant !== 'hidden'" [attr.data-variant]="variant">
      <span class="icon" aria-hidden="true">
        <ng-container [ngSwitch]="variant">
          <span *ngSwitchCase="'offlineEnabled'">🔴</span>
          <span *ngSwitchCase="'offlineLocalOnly'">🔴</span>
          <span *ngSwitchCase="'offlineAgentConnected'">🟣</span>
          <span *ngSwitchCase="'offlineHubBlocked'">🟠</span>
          <span *ngSwitchCase="'switchToCloud'">🟢</span>
          <span *ngSwitchCase="'serverAvailable'">🟢</span>
          <span *ngSwitchCase="'offlineDisabled'">⚠️</span>
        </ng-container>
      </span>
      <span class="text">{{ messageKey ? (messageKey | transloco:messageParams) : message }}</span>
      <span class="spacer"></span>
      <!-- RECON-6b — the breakdown renders ALONGSIDE the action, not
           instead of it. The old "and no action" condition made sense
           when meta was only ever a staleness note and the action was
           the switch; now meta carries "N to upload / N blocked / N need
           attention", so suppressing it whenever a Review button appears
           hid the counts in exactly the state they were added for. -->
      <span class="meta" *ngIf="metaLabel || metaLabelKey">{{ metaLabelKey ? (metaLabelKey | transloco:metaLabelParams) : metaLabel }}</span>
      <!-- RETRY-1 — secondary action. Review used to REPLACE "Go online"
           whenever anything had failed, which meant one unsyncable row
           held the whole terminal on the hub with no way forward. It sits
           beside the primary action now: the operator can still inspect
           what did not sync, but is never blocked by it. -->
      <button *ngIf="secondaryLabel" class="of2-banner-action of2-banner-secondary"
              (click)="onSecondaryAction()">
        {{ secondaryLabel }}
      </button>
      <button *ngIf="actionLabel || actionLabelKey" class="of2-banner-action"
              (click)="onAction()" [disabled]="actionInFlight">
        {{ actionLabelKey ? (actionLabelKey | transloco) : actionLabel }}
      </button>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .of2-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.3;
      color: #fff;
      user-select: none;
    }
    .of2-banner[data-variant="offlineEnabled"]          { background: #B45309; }
    .of2-banner[data-variant="offlineLocalOnly"]        { background: #B45309; }
    .of2-banner[data-variant="offlineAgentConnected"]   { background: #6D28D9; }
    .of2-banner[data-variant="offlineHubBlocked"]       { background: #C2410C; }
    .of2-banner[data-variant="switchToCloud"]           { background: #047857; }
    /* Distinct from switchToCloud: the link is back but NOT yet confirmed,
       and the terminal is still trading on the hub. Teal reads as
       "improving" without claiming the switch has happened. */
    .of2-banner[data-variant="serverAvailable"]         { background: #0F766E; }
    .of2-banner[data-variant="offlineDisabled"]         { background: #4B5563; }
    .icon  { font-size: 14px; }
    .text  { flex: 0 1 auto; }
    .spacer{ flex: 1 1 auto; }
    .meta  { opacity: 0.85; font-weight: 400; font-size: 12px; margin-right: 4px; }
    .of2-banner-action {
      background: rgba(255, 255, 255, 0.18);
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.35);
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 120ms ease;
    }
    .of2-banner-action:hover:not(:disabled) { background: rgba(255, 255, 255, 0.28); }
    .of2-banner-action:disabled             { opacity: 0.7; cursor: default; }
    .of2-banner-secondary {
      background: transparent;
      border-color: rgba(255, 255, 255, 0.5);
      font-weight: 500;
    }

  `],
})
export class OfflineBannerComponent implements OnInit, OnDestroy {
  variant: BannerVariant = 'hidden';
  message = '';
  /** Optional Transloco key for the banner message; template prefers it over `message`. */
  messageKey: string | null = null;
  messageParams: Record<string, unknown> | undefined = undefined;
  metaLabel = '';
  /** Optional Transloco key for the meta label. */
  metaLabelKey: string | null = null;
  metaLabelParams: Record<string, unknown> | undefined = undefined;
  actionLabel: string | null = null;
  /** Optional Transloco key for the action button label. */
  actionLabelKey: string | null = null;
  actionInFlight = false;

  /** RECON-10 — previous drainable depth, for the drain-complete edge. */
  private lastDrainable: number | null = null;

  /**
   * RECON-10 — treat a completed drain as proof the link is back.
   *
   * `NetworkStatusService` requires 5 consecutive probes over 60s before
   * it will declare recovery, which is right for a lone terminal guessing
   * from heartbeats. But if the hub has just POSTed an entire outbox to
   * the cloud and had it accepted, the link is not a guess — that is
   * stronger evidence than any number of heartbeats.
   *
   * Without this the operator watched "confirming the link before
   * switching back" for the rest of the 60s AFTER the upload had visibly
   * finished, with no button and nothing to do. Reported as: the sync
   * completes, then the banner just sits there and eventually hides.
   *
   * Only fires on the >0 → 0 transition, so a terminal that never had a
   * backlog is unaffected and still waits out the normal confirmation.
   */
  private confirmRecoveryOnDrainComplete(): void {
    const drainable = Math.max(0, this.pendingSync - this.quarantinedSync);
    const had = this.lastDrainable;
    this.lastDrainable = drainable;

    if (had === null || had <= 0 || drainable > 0) return;
    if (this.net.current() === 'online') return;

    // eslint-disable-next-line no-console
    console.info('[net] outbox drained to the cloud — treating as proof the link is back');
    void this.net.acceptCorroboratedRecovery();
  }

  /** RETRY-1 — optional second action, shown left of the primary one. */
  secondaryLabel: string | null = null;
  private secondaryAction: (() => void) | null = null;

  onSecondaryAction(): void {
    this.secondaryAction?.();
  }
  /**
   * OF-FIX-A1 Issue 2 — populated when the pre-flight bundle check for
   * the Local Hub refuses the handoff. Cleared on dismiss or on the
   * next successful action.
   */
  private pendingAction: (() => void) | null = null;

  private sub: Subscription | null = null;

  /**
   * RECON-14 — forces a re-render after an action changes state.
   *
   * `render()` writes `variant` / `message` / `actionLabel` as FIELDS, so
   * they only change when the subscription fires — and the subscription's
   * inputs are network state, store, hub reachability and hub stats.
   * Pressing "Go online" changes none of those: it flips a latch inside
   * HubRoutingService. So the banner sat on "Go online" until the next
   * hub poll happened to arrive (up to 30s when the queue is idle),
   * which read as the button doing nothing at all.
   */
  private readonly refresh$ = new BehaviorSubject<void>(undefined);

  // ── RECON-3 — outbox depth, straight off the hub heartbeat ────────
  /** Rows awaiting replay (includes the quarantined subset). */
  pendingSync = 0;
  /** Rows that can never drain without an operator decision. */
  failedSync = 0;
  /** Rows blocked behind a terminal action on the same check. */
  quarantinedSync = 0;
  /** Edge #44 — why the hub's last upload got no good reply (a category, e.g. `http_422`). */
  private lastDrainError: string | null = null;

  constructor(
    private net: NetworkStatusService,
    private store: StoreContextService,
    private hub: HubDiscoveryService,
    private hubRouter: HubRoutingService,
    private hubOrigin: HubOriginService,
    private handoff: HubHandoffService,
    private staleness: HubStalenessService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private policy: ReconnectPolicyService,
  ) { }

  ngOnInit(): void {
    this.sub = combineLatest([
      this.net.state$,
      this.store.activeStore$,
      this.hub.reachable$,
      this.hub.mixedContentBlocked$,
      this.hubOrigin.mode$,
      this.refresh$,
      // RECON-3 — the hub has reported outbox depth on every heartbeat
      // since OF-FIX-C1, and nothing has ever read it. An outbox that is
      // stuck, wedged, or accumulating dead-lettered rows was invisible
      // to every terminal on the floor.
      this.hub.stats$,
      // OFP-079 — the router's inputs the ones above miss: the shell's
      // hub-role snapshot, the hub's refusal and the hub device's liveness.
      // The hub device's "local hub not running" must show the moment the
      // shell reports the role, not at the next unrelated change. (Only the
      // extras — routingInputs$() would repeat net/store/reachability and
      // render twice per change.)
      this.hubRouter.hubSignals$(),
    ]).subscribe(([state, activeStore, hubReachable, mixedBlocked, originMode, , stats]) => {
      // OF-FIX-AUTOHUB-1 — ask the router directly instead of reading the
      // latched `active$`.
      //
      // That subject was only ever updated by `recomputeActive()`, and the
      // only thing calling it was the banner's own "Switch to Local Hub"
      // button. So the flag did not describe reality — it described
      // whether somebody had clicked. Routing had already moved to the
      // hub the moment the network went down. `decide()` is a pure
      // function over current state, so reading it here is both honest
      // and loop-free (unlike feeding a subject back into its own
      // combineLatest).
      const hubActive = this.hubRouter.decide() === 'hub';
      this.pendingSync = stats?.pendingSyncCount ?? 0;
      this.lastDrainError = stats?.lastDrainError ?? null;
      this.failedSync = stats?.failedSyncCount ?? 0;
      this.quarantinedSync = stats?.quarantinedSyncCount ?? 0;
      this.confirmRecoveryOnDrainComplete();
      this.render({
        state,
        offlineEnabled: activeStore?.offlineEnabled === true,
        hubConfigured: !!activeStore?.offlineHubUrl,
        hubUrl: activeStore?.offlineHubUrl ?? null,
        hubReachable, mixedBlocked, hubActive, originMode,
      });
      // OF-FIX-A2 — Explicit markForCheck after render(). The component
      // is ChangeDetectionStrategy.OnPush, which means mutating
      // template-bound fields (variant, message, actionLabel) from
      // inside a plain RxJS subscription does NOT trigger a re-render
      // automatically. Before this line, a real WAN drop would:
      //   1. Fail the heartbeat probe → state$ emits 'offline'
      //   2. combineLatest fires → render() correctly sets
      //      variant = 'offlineLocalOnly' / 'offlineAgentConnected' / etc.
      //   3. View stays at the previous variant (usually 'hidden')
      //      because OnPush never tells Angular to repaint.
      // The Cowork smoke test of OF-FIX-A1 surfaced this: all state
      // transitions were correct in memory, banner just never
      // repainted. markForCheck() tells Angular to re-run CD on the
      // next tick and the view catches up.
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onAction(): void {
    if (this.actionInFlight || !this.pendingAction) return;
    this.actionInFlight = true;
    try {
      this.pendingAction();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[OF-2F banner] action failed:', err);
    } finally {
      // RECON-6b (2026-08-22) — ALWAYS clear the flag.
      //
      // It used to be cleared only when the action threw synchronously,
      // which was survivable while the only actions either navigated the
      // page away (`doSwitchToCloud`) or cleared it themselves
      // (`doSwitchToHub`). RECON-5's "Review" action does neither: it is
      // a router navigation, so the page never unloads and this banner —
      // which now lives in AppComponent — stays mounted with the flag
      // stuck true. The button then rendered permanently disabled and,
      // worse, labelled "Checking hub…" (see the template note), so the
      // one control offering the stuck rows could never be pressed
      // again. Observed on a real terminal.
      //
      // Every current action is synchronous or unloads the page, so
      // clearing here is correct; a genuinely async action would need to
      // own the flag itself, as the commented-out bundle check did.
      this.actionInFlight = false;
      // RECON-14 — re-evaluate straight away. An action changes state the
      // subscription does not watch (a latch in HubRoutingService, a
      // navigation), so without this the banner keeps showing whatever it
      // last rendered until the next hub poll — up to 30s of a button
      // that looks like it did nothing.
      this.refresh$.next();
    }
  }


  private render(ctx: {
    state: NetworkState;
    offlineEnabled: boolean;
    hubConfigured: boolean;
    hubUrl: string | null;
    hubReachable: boolean;
    mixedBlocked: boolean;
    hubActive: boolean;
    originMode: 'cloud' | 'hub';
  }): void {
    const {
      state, offlineEnabled, hubConfigured, hubUrl, hubReachable, mixedBlocked, hubActive, originMode,
    } = ctx;
    this.metaLabel = '';
    this.metaLabelKey = null;
    this.metaLabelParams = undefined;
    this.actionLabel = null;
    this.actionLabelKey = null;
    this.pendingAction = null;
    this.secondaryLabel = null;
    this.secondaryAction = null;
    // RECON-6b — a new variant means the previous action's context is
    // gone, so any leftover busy flag is stale by definition. Belt to the
    // `finally` above.
    this.actionInFlight = false;
    // Reset previous key state — assignments below set whichever variant fires
    this.messageKey = null;
    this.messageParams = undefined;

    // RECON-9 — everything is done and we are waiting only on a person.
    //
    // `Manual` means the switch back is a deliberate act, so this is the
    // state where the operator finally gets a button. Shown BEFORE the
    // drain banner below because the two are mutually exclusive: this one
    // only fires once the backlog is clear.
    //
    // If anything is still unresolved the button is replaced by Review —
    // same rule as the switch-back gate: going online with rows the cloud
    // never accepted means checks read back without the money on them.
    if (this.hubRouter.isAwaitingManualRelease()) {
      this.variant = 'switchToCloud';
      this.metaLabel = this.backlogMeta();

      // RETRY-1 — never block the switch on a failed row.
      //
      // Failed rows are TERMINAL: the cloud gave a definitive 4xx and
      // will give it again, so they do not clear on their own. Gating
      // "Go online" behind them therefore did not protect anything — it
      // just held the terminal on the hub indefinitely while an operator
      // was asked to adjudicate a 422 mid-service. Retryable codes are
      // now retried automatically upstream (RETRY-1), so anything still
      // here genuinely needs a person — later, not now.
      this.message = this.failedSync > 0
        ? `Everything that could be uploaded has been. `
          + `${this.failedSync} action${this.failedSync === 1 ? '' : 's'} could not be sent and `
          + `${this.failedSync === 1 ? 'needs' : 'need'} attention.`
        : 'Everything has been uploaded. This terminal is ready to go back online.';
      // RECON-14 — acknowledge the press immediately.
      //
      // The first attempt set a `switching` flag and relied on
      // `actionLabel` being recomputed — but `actionLabel` is written by
      // `render()`, which the click does not re-run, so the label never
      // changed. Write it directly, then push a refresh so the banner
      // re-evaluates at once instead of waiting for the next hub poll.
      this.actionLabel = 'Go online';
      this.pendingAction = () => {
        this.actionLabel = 'Switching…';
        this.secondaryLabel = null;
        this.cdr.markForCheck();
        this.hubRouter.releaseToCloud();
        this.refresh$.next();
      };
      if (this.failedSync > 0) {
        this.secondaryLabel = 'Review';
        this.secondaryAction = () => { void this.router.navigate(['/pos/sync-issues']); };
      }
      return;
    }

    // RECON-4 — cloud confirmed, but the hub still holds writes it has
    // not handed over, so routing is deliberately still pointed at the
    // hub. Shown BEFORE the switch-back prompt below: until the backlog
    // clears there is nothing for the operator to decide, and a "switch
    // back" button here would offer exactly the action that loses sight
    // of the money still queued.
    if (this.hubRouter.isHoldingForDrain()) {
      // RECON-7 — `Automatic` applies the identical drain gate but does
      // not narrate it. Right for a single-terminal site with nobody to
      // inform; the routing behaviour is unchanged either way, so this
      // only decides whether the floor is told.
      if (!this.policy.announcesProgress()) {
        this.variant = 'hidden';
        this.message = '';
        return;
      }
      this.variant = 'serverAvailable';
      // RECON-8 — do not claim to be syncing when the hub says it cannot
      // reach the server. This terminal being online proves nothing about
      // the hub's own link, and when they disagree the backlog simply
      // cannot move. Saying "the hub is syncing" while the count sits
      // frozen reads as broken and gives an operator nothing to act on.
      if (this.hub.hubCannotReachCloud() && this.lastDrainError?.startsWith('http_4')) {
        // Edge #44 — the hub reached the server and the server refused the
        // whole upload (`lastDrainError` http_4xx). "Check the hub's
        // internet" would send the operator after the wrong thing.
        this.messageKey = 'pos.offline.msg.hubUploadRefused';
        this.messageParams = { count: this.pendingSync };
      } else if (this.hub.hubCannotReachCloud()) {
        this.message =
          `Store hub cannot reach the server — ${this.pendingSync} action` +
          `${this.pendingSync === 1 ? '' : 's'} waiting. Keep working; check the hub's internet.`;
      } else {
        this.message =
          `Server reconnected — uploading ${this.pendingSync} action${this.pendingSync === 1 ? '' : 's'} ` +
          `before switching back. Keep working; nothing is lost.`;
      }
      this.metaLabel = this.backlogMeta();
      return;
    }

    // OF-2F — Switch-back-to-cloud prompt on hub origin when WAN is up.
    // (Drain gate now lives in HubRoutingService.decide() — RECON-4.)
    if (originMode === 'hub' && state === 'online') {
      this.variant = 'switchToCloud';
      this.messageKey = 'pos.offline.msg.internetRestored';
      this.actionLabelKey = 'pos.offline.msg.switchBackCloud';
      this.pendingAction = () => this.doSwitchToCloud();

      // RECON-7 — refuse the switch while money is unresolved.
      //
      // Deliberately NOT done until RECON-5 existed. Blocking the switch
      // with nowhere to resolve the rows would have stranded the terminal
      // on the hub with no way forward, which is why the earlier pass
      // showed the count and let the operator through anyway. Now there
      // is a screen, so the honest answer is: deal with it first.
      //
      // Only `failed` blocks. Quarantined rows release themselves once
      // their blocker is resolved, and `pending` is just work in flight —
      // gating on either would stop a healthy store going back online.
      // RETRY-1 — see the note on the Manual branch: a terminal row never
      // clears, so blocking on it strands the terminal rather than
      // protecting anything. Surfaced, not enforced.
      if (this.failedSync > 0) {
        this.secondaryLabel = 'Review';
        this.secondaryAction = () => { void this.router.navigate(['/pos/sync-issues']); };
      }

      // RECON-3 — surface the backlog beside the switch action.
      //
      // Deliberately NOT disabling the button: gating the switch on a
      // clean drain is the completion gate (Phase 3), and it needs the
      // reconciliation flow behind it — a single permanently-failed row
      // would otherwise strand the terminal on the hub with no way
      // forward. Until that exists, the operator gets the number and
      // makes the call. Switching with rows outstanding means the cloud
      // does not yet hold them, so checks can briefly read back without
      // the payments just taken.
      this.metaLabel = this.backlogMeta();
      this.offerReconciliationIfStuck();
      return;
    }

    if (state === 'offline') {
      // OFP-079 — the router blocked for a reason only it can see: the hub
      // answered but refuses requests, or THIS terminal is the hub and its
      // own server is down. Name it. This banner is the only offline surface
      // on the order and settle screens, where the POS shell's lock overlay
      // is not mounted, and on the hub device it used to read "Offline —
      // Local Hub" while every tap failed against a closed loopback port.
      const blockKey = hubBlockReasonKey(this.hubRouter.blockReason());
      if (blockKey) {
        this.variant = 'offlineLocalOnly';
        this.messageKey = `${blockKey}.msg`;
        // The hub's own words (a 7.4.x shell's bind error), shown as-is.
        const detail = this.hubRouter.blockDetail();
        if (detail) {
          this.metaLabelKey = 'pos.offline.block.detail';
          this.metaLabelParams = { detail };
        }
        return;
      }

      if (!offlineEnabled) {
        // D1 hard-fail: store doesn't have offline enabled AND cloud is down.
        this.variant = 'offlineDisabled';
        this.messageKey = 'pos.offline.msg.noInternetDisabled';
        return;
      }

      // OF-2F — Hub origin + offline = actively running in Local Hub Mode.
      // Multi-mode: also show agent-connected when hub routing is active
      // (hubActive=true after user clicks "Switch to Local Hub" while
      // staying on the cloud origin).
      if (originMode === 'hub' || hubActive) {
        // RECON-3 — the cloud has started answering again, but recovery
        // is not yet confirmed (NetworkStatusService requires several
        // consecutive successes over ~60s). Announce it and keep
        // trading: this is the "just show that the server is available"
        // step. Nothing about routing changes here, so the floor carries
        // on exactly as it did a moment ago.
        if (this.net.isServerAvailableWhileOffline()) {
          this.variant = 'serverAvailable';
          this.message = this.pendingSync > 0
            ? `Server connection restored — ${this.pendingSync} action${this.pendingSync === 1 ? '' : 's'} still to upload. Keep working; the hub is syncing.`
            : 'Server connection restored — confirming the link before switching back.';
          this.metaLabel = this.backlogMeta();
          this.offerReconciliationIfStuck();
          return;
        }

        this.variant = 'offlineAgentConnected';
        const stale = this.staleness.stale();
        const staleNote = stale
          ? ' Some data may be older than usual while the hub catches up.'
          : '';
        this.message = `Offline — Local Hub. Actions save here and sync to cloud when internet returns.${staleNote}`;
        // RECON-3 — depth first: on a busy service this is the number
        // that tells an operator whether the hub is keeping up or
        // silently accumulating. `failed` and `quarantined` are shown
        // SEPARATELY and never folded into "pending" — they will not
        // clear on their own and mean something different.
        const backlog = this.backlogMeta();
        if (backlog) {
          this.metaLabel = backlog;
          this.metaLabelKey = null;
          this.metaLabelParams = undefined;
          this.offerReconciliationIfStuck();
          return;
        }
        if (stale) {
          const resources = this.staleness.resources();
          this.metaLabel = resources.length > 0
            ? `Stale: ${resources.join(', ')}`
            : 'Stale data';
        }
        return;
      }

      // OF-FIX-AUTOHUB-1 — failover to the hub is AUTOMATIC. There is no
      // "Switch to Local Hub" button any more, and this branch no longer
      // offers one.
      //
      // A WAN drop is not a choice the operator gets to make. By the time
      // a banner is noticed and a button found, orders have already
      // failed — and during a rush nobody reads banners. There is also no
      // downside to deciding for them: the hub is strictly better than
      // failing, and `HubRoutingService.decide()` has always routed there
      // on its own regardless of the button, which is exactly why the
      // button was decorative.
      //
      // The asymmetry with the RETURN journey is deliberate, not an
      // oversight: going offline is forced and safe, whereas coming back
      // early is dangerous (the cloud has not yet received the offline
      // writes, so a settled check reads back unpaid). That direction
      // keeps its gate and, in Manual mode, its button.
      //
      // Reached when the hub is reachable but this render ran before the
      // routing decision agreed — same reality as offlineAgentConnected
      // above, so say the same thing rather than implying a pending
      // choice.
      if (hubConfigured && hubReachable && hubUrl) {
        this.variant = 'offlineAgentConnected';
        this.message =
          'Offline — using the store hub. Actions save here and sync to '
          + 'cloud when internet returns.';
        this.metaLabel = this.backlogMeta();
        this.offerReconciliationIfStuck();
        return;
      }

      // Hub configured but blocked by mixed-content / unreachable.
      if (hubConfigured && mixedBlocked) {
        this.variant = 'offlineHubBlocked';
        this.messageKey = 'pos.offline.msg.offlineLocalOnly';
        return;
      }

      // D1 hard-fail: no hub configured OR hub unreachable.
      // (Was "offlineLocalOnly / Actions will sync" — that promise no longer
      // holds without the browser IDB queue. Show the same hard-fail copy as
      // offlineDisabled.)
      this.variant = hubConfigured ? 'offlineLocalOnly' : 'offlineEnabled';
      this.message = `Cannot reach store hub. POS actions are disabled until the connection is restored.`;
      return;
    }

    this.variant = 'hidden';
    this.message = '';
  }

  /**
   * RECON-3 — the outbox summary shown beside the banner text.
   *
   * `failed` and `quarantined` are deliberately called out on their own
   * rather than rolled into the pending total. Pending shrinks by itself;
   * these two do not. A cashier seeing "214 to upload" tick down is
   * reassured correctly, whereas "214" that includes 12 permanently
   * stuck rows would be a lie that only surfaces at cash-up.
   */
  private backlogMeta(): string {
    const parts: string[] = [];
    if (this.pendingSync > 0) parts.push(`${this.pendingSync} to upload`);
    if (this.quarantinedSync > 0) parts.push(`${this.quarantinedSync} blocked`);
    if (this.failedSync > 0) parts.push(`${this.failedSync} need attention`);
    return parts.join(' · ');
  }

  /**
   * RECON-5 — offer the reconciliation screen whenever something is
   * stuck. Attached to every variant that can carry a backlog, because
   * "N need attention" with no way to act on it is the state this whole
   * change exists to remove.
   */
  private offerReconciliationIfStuck(): void {
    // RECON-9c — only `failed` rows are actionable, so only they earn a
    // button. Quarantined rows cannot be resolved directly — they release
    // themselves once their failed ancestor is dealt with — so offering
    // Review for "2 blocked" alone sent the operator to a screen where
    // the only thing they could do was look. Every quarantined row
    // implies a failed ancestor anyway, so nothing is lost by keying on
    // the failed count.
    if (this.failedSync <= 0) return;
    this.secondaryLabel = 'Review';
    this.secondaryAction = () => { void this.router.navigate(['/pos/sync-issues']); };
  }

  // ── Switch handlers ────────────────────────────────────────

  // OF-FIX-AUTOHUB-1 — `doSwitchToHub` deleted along with the button.
  //
  // It had been reduced to `recomputeActive()` plus a spinner reset: the
  // pre-flight bundle check and the cross-origin redirect it used to do
  // were commented out when the SPA stopped moving to the hub origin.
  // What remained only refreshed the banner's own label, which is why the
  // button looked like a choice and was not one.

  private doSwitchToCloud(): void {
    // OF-2G — Reverse handoff. Symmetric to OF-2F's cloud→hub flow:
    // pack the current POS + active-store context into the redirect
    // URL fragment so the cashier doesn't have to PIN in again on
    // arrival. HubHandoffService already handles both directions
    // because the hub's URL is what it accepts; here we invert it.
    //
    // OF-FIX-RTC-1 (2026-08-22) — this used to redirect to the bare
    // `cloudAppUrl` ROOT:
    //
    //     const withHandoff = this.handoff.buildRedirectUrl(cloudUrl);
    //
    // and `buildRedirectUrl` appends `/#handoff=…` to whatever base it
    // is handed, so the operator landed on `https://<host>/`. The POS
    // routes redirect `'' → 'pos'`, which IS the PIN screen — with no
    // store code in the URL and, on a dev/QA build, on the ADMIN host
    // (the old `environment.ts` value), whose shell then says "No store
    // selected". Reported from UAT as: coming back online kicks you to
    // login and you have to sign in again.
    //
    // Target `/pos/terminal` instead. The handoff carries the POS token
    // and POS auth, so `posAuthGuard` admits straight into the shell —
    // and when the token HAS expired, that guard's own fallback now has
    // `pos_standalone_code` to work with and lands on `/pos/s/<code>`
    // rather than the storeless `/pos`.
    const base = ((environment as any).cloudAppUrl ?? '').trim();
    if (!base) {
      // eslint-disable-next-line no-console
      console.warn('[OF-2G banner] cloudAppUrl is not configured — cannot switch back.');
      return;
    }

    let target: string;
    try {
      target = new URL('/pos/terminal', base).href;
    } catch {
      // eslint-disable-next-line no-console
      console.warn('[OF-2G banner] cloudAppUrl is malformed:', base);
      return;
    }

    const withHandoff = this.handoff.buildRedirectUrl(target);
    // eslint-disable-next-line no-console
    console.info('[OF-2G banner] redirecting back to cloud with handoff:', withHandoff ?? target);
    window.location.assign(withHandoff ?? target);
  }
}
